import type { useDisclosure } from "@mantine/hooks";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { useI18nContext } from "~/i18n/i18n-react";
import { api } from "~/utils/api";
import { Button } from "../ui/button";

interface RoomDeletionConfirmationModalProps {
	disclosure: ReturnType<typeof useDisclosure>;
	roomId: string;
}

export const RoomDeletionConfirmationModal = ({
	disclosure,
	roomId,
}: RoomDeletionConfirmationModalProps) => {
	const [opened, { close }] = disclosure;
	const { LL } = useI18nContext();
	const utils = api.useUtils();

	const mutation = api.room.end.useMutation({
		onSuccess: async () => {
			const { useSocketV2Store } = await import("~/stores/socket");
			useSocketV2Store.getState().broadcastAll({
				type: "room-ended",
				data: { roomId },
			});
			utils.room.invalidate();
			close();
		},
	});

	const handleDelete = () => {
		mutation.mutate({ roomId });
	};

	return (
		<AlertDialog onOpenChange={close} open={opened}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-destructive" />
						{LL.DELETE_CONFIRMATION_MODAL.TITLE()}
					</AlertDialogTitle>
					<AlertDialogDescription>
						{LL.DELETE_CONFIRMATION_MODAL.DESCRIPTION()}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={mutation.isPending}>
						{LL.DELETE_CONFIRMATION_MODAL.CANCEL()}
					</AlertDialogCancel>
					<Button
						className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						disabled={mutation.isPending}
						onClick={handleDelete}
					>
						{mutation.isPending ? (
							<span className="flex items-center gap-2">
								<Loader2 className="h-4 w-4 animate-spin" />
								{LL.DELETE_CONFIRMATION_MODAL.DELETE()}
							</span>
						) : (
							LL.DELETE_CONFIRMATION_MODAL.DELETE()
						)}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
